import { findTransition, isInMatch, MATCH_STATE } from './machine';
import type { TransitionEvent } from './machine';
import type { MatchPurpose, MatchState } from '../types';

/**
 * B spec §2.2 / §2.4 — **the Matches tab's decisions** (build unit B-5).
 *
 * The A precedent is `domain/file.ts`: Home's membership questions live in `domain/` so two
 * surfaces can never disagree about the same match, and so `MatchState` values are spelled only
 * where the vocabulary is owned (SES-1). B's Matches home asks two membership questions A's Home
 * never asks — B has no distillation queue and A has no coach cards — so they live in their own
 * module rather than being grafted onto A's (`reuse contract §5.3: add alongside, don't alter`).
 *
 * ── The three groups, and why they overlap ───────────────────────────────────
 *
 * | Group | Membership | Owner |
 * |---|---|---|
 * | In progress | `preMatch` ∨ in-match (`resume.isResumable`'s law) | `app/resume.ts`, reused |
 * | Needs finishing | {@link needsFinishingB} | here |
 * | History | {@link isMatchHistoryB} | here |
 *
 * Membership on the In-progress card is one question; **where a tap on it goes is a second one**,
 * and until 2026-08-19 the two were the same predicate — see {@link openTargetForB}.
 *
 * **Needs-finishing is deliberately a subset of history, rendered twice.** The queue is a
 * call-to-action ("one tap back to the match record", §2.2); history is the complete reviewable
 * record (scenario 33). Hiding queue members from history would make history rows appear only
 * when their match stops needing attention — a list whose membership changes for a reason the
 * user cannot see. The queue empties by `CompleteMatch` (F-1) or by discarding/finalizing
 * drafts; history never loses a row.
 */

/**
 * §2.2 — *"history lists and the match record key on state ∈ {fullTime…archived}"*: every match
 * from the full-time whistle on, including abandoned ones (an abandoned match IS `fullTime` with
 * `abandoned = true`, §2.2's engine law) and including `triage`/`distill` — states B never
 * enters, but which an imported A archive can legitimately hold (scenario 35).
 *
 * Written as the complement of "not yet played or being played" rather than the positive list,
 * because the positive list would be a verbatim copy of `canSaveBoardAsTemplate`
 * (`domain/file.ts`) — same four states, different meaning — and a near-duplicate with a
 * different name is exactly what the reuse contract's duplication gate flags. `idle` and
 * `preMatch` are excluded by name (the `canSaveBoardAsTemplate` doc's warning about negated
 * predicates admitting them): an unstarted match is nobody's history.
 */
export function isMatchHistoryB(state: MatchState): boolean {
  return state !== MATCH_STATE.idle && state !== MATCH_STATE.preMatch && !isInMatch(state);
}

/**
 * §2.2 — the **Needs-finishing queue**, under F-1's approved ruling: *"every match holding a
 * draft card, plus (while F-1 is approved) every `fullTime` match not yet completed."*
 *
 * - The `fullTime` arm includes abandoned matches. A's SES-6 exclusion protected A's users from
 *   being nagged **every launch** to distil a match they walked away from; B's queue is a list
 *   on a screen, never a prompt, and under F-1 `CompleteMatch` is the only way a B match leaves
 *   `fullTime` — excluding abandoned matches would strand them in the queue's negative space
 *   forever with no way out. The row's state label still reads *Abandoned* (`matchStateLabel`).
 * - The draft arm takes the committed fact as a parameter (`holdsDraftCard` — the caller joins
 *   `coachCards`, §4.5) rather than reading storage: this layer is pure.
 * - A **resumable** match never queues here even when it holds a draft (a halftime draft's
 *   match is the In-progress card's; the takeover shell owns it) — that is the
 *   {@link isMatchHistoryB} guard, since resumable and history are disjoint by construction.
 * - An `archived` match holding a draft would be listed; B-18's `ERR_CARD_DRAFTS` guard exists
 *   precisely so that state is unreachable (§6: completing over a draft would strand it), so
 *   the arm is not special-cased here — if the impossible row ever appears, surfacing it beats
 *   hiding it.
 */
export function needsFinishingB(state: MatchState, holdsDraftCard: boolean): boolean {
  return isMatchHistoryB(state) && (state === MATCH_STATE.fullTime || holdsDraftCard);
}

/**
 * The two surfaces an In-progress card can open onto. Named for the shells B actually has, not for
 * `MatchState`: the card's tap is a navigation, and the view that receives this value must not be
 * able to spell a state (SES-1 keeps `MatchState` inside `domain/`).
 */
export type OpenTargetB = 'setup' | 'takeover';

/**
 * **Where tapping this match opens** — or `null` if it does not belong on the In-progress card at
 * all. The fix for the defect Andrew reported on 2026-08-19:
 *
 * > *"When I tap the match, it takes me to the match in progress page, from which I cannot escape
 * > without abandoning the match."*
 *
 * ── One predicate was doing two jobs ─────────────────────────────────────────
 *
 * `resume.isResumable` (`preMatch ∨ isInMatch`) answered **both** *"does this match belong on the
 * In-progress card?"* and *"does tapping it resume into capture?"*. Those coincide for every state
 * except `preMatch` — which is exactly the state that was wrong. Narrowing `isResumable` would have
 * taken the card away with the destination; it is the card Andrew relies on to find the setup at
 * all, and `isMatchHistoryB` excludes `preMatch` by name, so there is no other list that would hold
 * it. So the two meanings are **split** and the membership half is left byte-for-byte alone:
 * `openTargetForB(state) !== null` is `isResumable`'s own law, re-expressed, and `homeB.test.ts`
 * asserts that equivalence over the closed state list in both directions — which is what makes this
 * a split rather than a second, drifting copy.
 *
 * ── This is not a new decision; it is the one the launch path already makes ───
 *
 * `AppB` has resolved a `preMatch` match to the **setup** overlay since B-7 (`resolveResume` →
 * `defaultSurfaceFor` → `SURFACE.setup`), because *"before this arm existed a kill mid-setup
 * relaunched into the takeover placeholder with no way back: a dead end"*. The Matches tap was the
 * second entry path to that same effect and it disagreed — `new-path-parity-single-source-the-effect`
 * in miniature. The two halves cannot be one function (this layer may not import `app/`), so the
 * agreement is pinned by a test that drives `defaultSurfaceFor` across every state instead.
 *
 * ── `preMatch` covers TWO situations and that is deliberate, not an oversight ─
 *
 * `StartCapture` writes nothing to the match row in discovery mode, so a match whose setup was never
 * finished and one that has *begun* and is waiting for the whistle are the same row, both `preMatch`
 * — which is why `takeoverSurfaceFor` has a `preKickoff` arm. There is no row-level discriminator to
 * key on, and inventing one (joining `hypotheses`, say) would be a third rule for the same question.
 * The reason none is needed: for a begun match to be on the Matches home at all, `AppB` must have
 * resolved to the tab shell or to the **setup** overlay — the takeover overlay offers no route back
 * to the tabs (see the report accompanying this unit) — and `AppB` sends a `preMatch` match to setup
 * on exactly the inputs this function does. Agreeing with the launch path is therefore not merely
 * consistent; there is no reachable state in which the two could sensibly differ.
 */
export function openTargetForB(state: MatchState): OpenTargetB | null {
  if (isInMatch(state)) return 'takeover';
  if (state === MATCH_STATE.preMatch) return 'setup';
  return null;
}

/**
 * Which transition would delete this match — or `null` if none may (Andrew's ruling, 2026-08-19).
 *
 * The ruling admits **two** populations and they are deleted by two different existing events:
 *
 * | Population | State | Event | Gate |
 * |---|---|---|---|
 * | *"a test match I abandoned"* | `fullTime`…`archived` | `DeleteMatch` | `abandoned` |
 * | *"never started"* — an unfinished setup | `preMatch` | `DiscardSetup` | none: nothing is under way to protect |
 *
 * **The second arm needed no engine work at all.** `DiscardSetup` has deleted an unstarted setup
 * since U-4, through this same `to: null` → `cascadeDeleteMatch` path, and `app/preMatch` already
 * exposes it — A's Pre-Match screen has always had the control. What B lacked was a *surface*, not
 * a capability, so nothing here widens the ruled addition.
 *
 * ── This is not a mirror of the engine's rule, it is a read of it ────────────
 *
 * A screen that decides for itself which matches are deletable is a second eligibility rule, and
 * the two drift. Both state halves are therefore answered by asking `TRANSITION_TABLE` itself
 * through `findTransition`; the only restated fact is `abandoned`, the same single boolean
 * `machine.transition`'s guard reads. There is nothing here for the engine to drift *from*.
 *
 * The two conditions on the first arm are **not** redundant with each other. `abandoned` alone
 * would offer the control on an in-match row if the flag ever appeared there; the table alone would
 * offer it on every completed match at `fullTime`, which is exactly the outcome the ruling excludes.
 *
 * The arms are checked in order but **cannot overlap**: `DiscardSetup` is legal only from
 * `preMatch` and `DeleteMatch` never is, so no state can answer both. `homeB.test.ts` asserts that
 * against the real table rather than leaving it as a claim about the order of two `if`s.
 *
 * And the control is a courtesy, never the enforcement: `machine.transition` refuses the dispatch
 * whatever this returns, which is what makes a stale render harmless (§2.2).
 */
export function deleteEventForB(state: MatchState, abandoned: boolean): DeleteEventB | null {
  if (abandoned && findTransition(state, 'DeleteMatch') !== undefined) return 'DeleteMatch';
  if (findTransition(state, 'DiscardSetup') !== undefined) return 'DiscardSetup';
  return null;
}

/**
 * The two deleting transitions, as the Matches home names them.
 *
 * A narrowed alias of `TransitionEvent` rather than the whole union: the value crosses into the
 * view and back out to a facade, and a type that could carry `Kickoff` would let a render dispatch
 * a clock anchor. Two members, both of which delete.
 */
export type DeleteEventB = Extract<TransitionEvent, 'DeleteMatch' | 'DiscardSetup'>;

/**
 * §2.4 — the purpose chip's copy, and (B-7's reuse) the two purpose cards' titles. In `domain/`
 * for `matchStateLabel`'s reason: one vocabulary, one owner. `null` — a match created before
 * the choice was made (or imported from a pre-v004 archive whose repair backfill was bypassed) —
 * renders no chip rather than a placeholder word.
 */
export function matchPurposeLabel(purpose: MatchPurpose | null): string | null {
  if (purpose === null) return null;
  return purpose === 'teamMatch' ? 'Team match' : 'Advance scout';
}
